import { Typography, Box, Button, CircularProgress } from "@material-ui/core"
import PropTypes from "prop-types"
import { useDispatch, useSelector } from "react-redux"
import { openSnackBar } from "../../../../../actions/snack-bar"
import { updateCrewStatusCall } from "../../../../../actions/billing"
import { clearCrewFromChip } from "../../../../../actions/crew"
import {
  setRoleForCrew,
  toggleShootDialog,
  setActiveShootRole,
} from "../../../../../actions/shoots"
import moment from "moment"
import useStyles from "./styles"
import AddIcon from "@material-ui/icons/Add"
import React, { Fragment, useMemo, useState } from "react"
import { toggleClickConfirmationDialog } from "../../../../../actions/billingDialogs"
import useBillingDisabled from "../../../../../hooks/useBillingDisabled"
import CopyLinkButton from "../../../Common/CopyLinkButton/index"

const ButtonsComponent = ({ position }) => {
  const [sendLoading, setSendLoading] = useState(false)
  const [acceptLoading, setAcceptLoading] = useState(false)
  const dispatch = useDispatch()
  const classes = useStyles()

  const authenticatedUser = useSelector(
    state => state.authentication.authenticatedUser
  )

  const isDisabled = useBillingDisabled()

  let statusDate = useMemo(
    () => {
      return moment(position[`${position.user_status}_date_time`]).format(
        "M/D/YY[ @ ]h:mm A"
      )
    },
    [position]
  )

  let status = position.user_status

  // *** SEND DEAL MEMO    ***
  const sendDealMemo = (data, shootRoleId, status) => {
    let dataToUPdateDetails = { requested: 1 }
    setSendLoading(true)
    dispatch(
      updateCrewStatusCall(
        data,
        shootRoleId,
        status,
        position.shoot_id,
        position.user_id,
        dataToUPdateDetails
      )
    )
      .then(() => {
        setSendLoading(false)
        dispatch(
          openSnackBar({
            message: "Email sent",
          })
        )
      })
      .catch(err => {
        console.log(err)
        setSendLoading(false)
        dispatch(
          openSnackBar({
            message: "Error sending email",
          })
        )
      })
  }

  const sendConfirmDialog = () => {
    let sendData = {
      action: "update-status-requested",
    }
    let data = {
      action: "send deal memo to this crew member",
      confirmationFunc: sendDealMemo,
      funcArgs: [sendData, position.shoot_role || position.id, "sent"],
    }
    dispatch(toggleClickConfirmationDialog(true, data))
  }
  // *** SEND DEAL MEMO END   ***

  // *** ACCEPT DEAL MEMO    ***

  const acceptCrew = (data, shootRoleId, status) => {
    let requestData = { ...data, available_date_time: moment().format() }
    let dataToUPdateDetails = { requested: 1, available: 1 }

    setAcceptLoading(true)
    dispatch(
      updateCrewStatusCall(
        requestData,
        shootRoleId,
        status,
        position.shoot_id,
        position.user_id,
        dataToUPdateDetails
      )
    )
      .then(() => {
        setAcceptLoading(false)
        dispatch(
          openSnackBar({
            message: "Accepted on behalf of crew member",
          })
        )
      })
      .catch(err => {
        console.log(err)
        setAcceptLoading(false)
        dispatch(
          openSnackBar({
            message: "Error marking crew member as accepted",
          })
        )
      })
  }

  const acceptConfirmDialog = () => {
    let acceptData = {
      action: "admin-accepts-deal-memo",
      deal_memo_signature: `${authenticatedUser.first_name} ${
        authenticatedUser.last_name
      } (Admin)`,
    }
    let data = {
      action: "make this crew member available for this shoot",
      confirmationFunc: acceptCrew,
      funcArgs: [acceptData, position.shoot_role || position.id, "available"],
    }
    dispatch(toggleClickConfirmationDialog(true, data))
  }
  // *** ACCEPT DEAL MEMO END   ***

  const addCrew = () => {
    dispatch(clearCrewFromChip())
    dispatch(setRoleForCrew(position))
    dispatch(
      toggleShootDialog({
        open: true,
        id: position.shoot_id,
      })
    )
    dispatch(
      setActiveShootRole(
        position.shoot_role ? position.shoot_role : position.id
      )
    )
  }

  //no user attached to the role
  if (!position.first_name && !position.last_name) {
    return (
      <Fragment>
        <Box display="flex" alignItems="center">
          <Button
            size="small"
            variant="outlined"
            color="primary"
            onClick={addCrew}
          >
            <AddIcon
              style={{ fontSize: "15px", fontWeight: "bold" }}
              disabled={isDisabled}
            />
            <Typography className={classes.buttonText} variant="button">
              Crew Member
            </Typography>
          </Button>
        </Box>
      </Fragment>
    )
    //email sent to crew
  } else if (status === "sent") {
    return (
      <Fragment>
        <Box className={classes.sentBox}>
          <Box display="flex" alignItems="center">
            <Typography variant="subtitle1" className={classes.dateText}>
              <span className={classes.sentText}>Sent</span> on {statusDate}
            </Typography>
          </Box>
          {acceptLoading ? (
            <Box
              width="80px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <CircularProgress size={20} />
            </Box>
          ) : (
            <Button
              variant="outlined"
              size="small"
              color="primary"
              onClick={acceptConfirmDialog}
              style={{ marginLeft: "10px" }}
            >
              <Typography className={classes.buttonText} variant="button">
                Accept
              </Typography>
            </Button>
          )}
        </Box>
      </Fragment>
    )
    //crew accepted
  } else if (
    status === "available" ||
    status === "approved" ||
    status === "bill_requested" ||
    status === "bill_accepted"
  ) {
    return (
      <Fragment>
        <Box
          display="flex"
          alignItems="center"
          flexWrap="wrap"
          justifyContent="flex-end"
        >
          <Typography variant="subtitle1" className={classes.dateText}>
            <span className={classes.acceptedText}>Accepted</span>
            {position.deal_memo_signature && (
              <span> by {position.deal_memo_signature}</span>
            )}
            {/* fallback to available date here... */}
            {position.available_date_time && (
              <span>
                {" "}
                on{" "}
                {moment(position.available_date_time).format(
                  "M/D/YY[ @ ]h:mm A"
                )}
              </span>
            )}
          </Typography>
        </Box>
      </Fragment>
    )
    //crew rejected
  } else if (status === "rejected") {
    return (
      <Fragment>
        <Box display="flex" alignItems="center">
          <Typography variant="subtitle1" className={classes.dateText}>
            <span className={classes.rejectedText}>Rejected</span> on{" "}
            {statusDate}
          </Typography>
        </Box>
      </Fragment>
    )
    //waiting for send deal memo or accept crew
  } else if (status === "unsent") {
    return (
      <Fragment>
        <Box className={classes.unsentBox}>
          {sendLoading ? (
            <Box
              width="160px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <CircularProgress size={20} />
            </Box>
          ) : (
            <Button
              variant="outlined"
              color="primary"
              onClick={sendConfirmDialog}
              style={{ marginRight: "10px" }}
              size="small"
            >
              <Typography className={classes.buttonText} variant="button">
                Send Deal Memo
              </Typography>
            </Button>
          )}

          {acceptLoading ? (
            <Box
              width="80px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <CircularProgress size={20} />
            </Box>
          ) : (
            <Button
              variant="outlined"
              color="primary"
              onClick={acceptConfirmDialog}
              className={classes.acceptButton}
              size="small"
            >
              <Typography className={classes.buttonText} variant="button">
                Accept
              </Typography>
            </Button>
          )}
        </Box>
      </Fragment>
    )
  } else {
    return ""
  }
}

ButtonsComponent.propTypes = {
  position: PropTypes.object,
}

export default ButtonsComponent
